import React, { useEffect } from 'react';
import LoaderSpinner from '../common/Loader';
import { useNavigate } from 'react-router-dom';
import { TextField, MenuItem, Select, FormControl } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import ReactSelect from "react-select";
import { emailPlaceholders, getAllTemplates, getRecipientGroups, sendBroadcast, sendIndividualEmails } from '../../store/actions/auth';
import { toast } from 'react-toastify';
import _ from 'lodash';
import { useSelector } from 'react-redux';
import AlertDialogModal from '../common/AlertDialog';
import { QUILL_MODULES } from '../../constant';
const useStyles = makeStyles((theme) => ({
    formRow: {
        display: 'flex',
        alignItems: 'flex-start',
        marginBottom: theme.spacing(3),
        [theme.breakpoints.down('sm')]: {
            flexDirection: 'column',
        },
    },
    label: {
        width: 160,
        minWidth: 160,
        textAlign: 'right',
        paddingRight: theme.spacing(2),
        paddingTop: 9,
        fontWeight: 500,
        color: '#333',
        flexShrink: 0,
        [theme.breakpoints.down('sm')]: {
            width: '100%',
            minWidth: 'unset',
            textAlign: 'left',
            paddingBottom: theme.spacing(0.5),
            paddingRight: 0,
        },
    },
    fieldWrapper: {
        flex: 1,
    },
    select: {
        width: '100%',
        backgroundColor: '#fff',
    },
    textField: {
        width: '100%',
        backgroundColor: '#fff',
    },
    quillWrapper: {
        flex: 1,
        '& .ql-container': {
            minHeight: 180,
            fontSize: '14px',
        },
    },
}));
const ComposeEmail = () => {
    const userDetails = useSelector((state) => state.auth.user);

    const classes = useStyles();
    const [showLoader, setShowLoader] = React.useState(false);
    const [formData, setFormData] = React.useState({
        recipients: [],
        templateName: '',
        subject: '',
        recipientEmailsList: [],
        body: '',
        selectedTemplate: '',
    });
    const [emailInput, setEmailInput] = React.useState('');
    const quillRef = React.useRef(null);
    const [recipientList, setRecipientList] = React.useState([]);
    const [templateList, setTemplateList] = React.useState([]);
    const [showAlert, setShowAlert] = React.useState(false);
    let navigate = useNavigate();

    const [emailPlaceholderList, setEmailPlaceholdersList] = React.useState([]);

    const isSellerOrAdmin = () => {
        const roleName = (userDetails?.Value?.roleName || '').toString().trim().toLowerCase();
        const role = (userDetails?.Value?.role || '').toString().trim().toLowerCase();
        const isSeller = roleName === 'seller' || role === 'seller';
        const isAdmin = roleName === 'admin' || role === 'admin';
        return isSeller || isAdmin;
    }

    useEffect(() => {
        fetchemailPlaceholders();
        fetchAllRecipientList();
        fetchTemplates();
    }, []);

    const handleChange = (field, value) => {
        setFormData((prev) => ({ ...prev, [field]: value }));
    };

    const fetchTemplates = async () => {
        setShowLoader(true);
        try {
            const response = await getAllTemplates();
            setTemplateList(response.data.EntityList || []);
        } catch (error) {
            toast.error('An error occurred while fetching template details.');
        } finally {
            setShowLoader(false);
        }
    };

    const isValidEmail = (email) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

    const addEmail = (email) => {
        if (!email) return;
        if (!isValidEmail(email)) {
            toast.error(`"${email}" is not a valid email address.`);
            return;
        }
        if (formData.recipientEmailsList.includes(email)) {
            setEmailInput('');
            return;
        }
        handleChange('recipientEmailsList', [...formData.recipientEmailsList, email]);
        setEmailInput('');
    };

    const removeEmail = (email) => {
        handleChange('recipientEmailsList', formData.recipientEmailsList.filter((e) => e !== email));
    };

    const handleEmailInputKeyDown = (e) => {
        if (e.key === 'Enter' || e.key === ',') {
            e.preventDefault();
            addEmail(emailInput.trim());
        }
    };

    const insertDynamicValue = (variable) => {
        if (!variable) return;
        const editor = quillRef.current?.getEditor();
        if (editor) {
            const range = editor.getSelection(true);
            editor.insertText(range ? range.index : editor.getLength(), variable);
        }
    };

    const validateForm = () => {
        if (!isSellerOrAdmin() && (!formData.recipients || formData.recipients.length === 0)) {
            toast.error('At least one recipient is required.');
            return false;
        }
        if (isSellerOrAdmin() && formData.recipientEmailsList.length === 0) {
            toast.error('Recipient email(s) are required for your role.');
            return false;
        }
        if (!formData.subject.trim()) {
            toast.error('Subject is required.');
            return false;
        }
        if (!formData.body.trim()) {
            toast.error('Body is required.');
            return false;
        }
        return true;
    };

    const handleSendClick = () => {
        if (emailInput.trim()) {
            if (!isValidEmail(emailInput.trim())) {
                toast.error(`"${emailInput.trim()}" is not a valid email address.`);
                return;
            }
            addEmail(emailInput.trim());
            return; // re-click to confirm after flush
        }
        if (!validateForm()) return;
        setShowAlert(true);
    };

    const handleSend = async () => {
        setShowAlert(false);
        setShowLoader(true);
        try {
            // Simulate API call to send email
            let payload = {
                sendMode: 'broadcast',
                recipientGroupIds: _.map(formData.recipients, 'value'),
                subject: formData.subject.trim(),
                body: formData.body,
            };
            if (isSellerOrAdmin()) {
                payload = {
                    ...payload,
                    sendMode: 'Individual',
                    recipientEmails: formData.recipientEmailsList.join(', '),
                }
            }
            let response = await (isSellerOrAdmin() ? sendIndividualEmails(payload) : sendBroadcast(payload));
            if (response?.data?.Status === 200) {
                toast.success('Email sent successfully!');
            } else {
                toast.error('Failed to send email. Please try again.');
            }
        } catch (error) {
            toast.error('An error occurred while sending the email.');
        } finally {
            setShowLoader(false);
        }
    };

    const fetchAllRecipientList = async () => {
        setShowLoader(true);
        try {            // Simulate API call to fetch recipient list
            let response = await getRecipientGroups(); // Replace with actual API endpoint
            let listWithAllOption = response.data.EntityList.map(group => ({ ...group, value: group.id, label: `${group.name} - ${group.description}` }));
            setRecipientList(listWithAllOption);
        } catch (error) {
            toast.error('An error occurred while fetching recipient groups.');
        } finally {
            setShowLoader(false);
        }
    }

    const fetchemailPlaceholders = async () => {
        try {
            const response = await emailPlaceholders();
            if (response?.data?.Status === 200) {
                setEmailPlaceholdersList(response?.data?.EntityList || []);
            }
        } catch (error) {
            console.error('Error fetching email placeholders:', error);
        }
    };

    const handleMultipleSelectChange = (value, field) => {
        setFormData((prev) => ({ ...prev, [field]: value }));
    };

    const handleTemplateSelect = (e) => {
        const selected = templateList.find(t => t.name === e.target.value);
        if (selected) {
            setFormData((prev) => ({
                ...prev,
                selectedTemplate: selected.name,
                templateName: selected.name,
                subject: selected.subject,
                body: selected.body,
            }));
        } else {
            setFormData((prev) => ({
                ...prev,
                selectedTemplate: '',
                templateName: '',
                subject: '',
                body: '',
            }));
        }
    };

    return (
        <main>
            <div className="main__container">
                <div className="main__tile_heading cus-btn-include-sec">
                    <span>
                        Compose Email
                        <div className='infoPermission'>
                            Create and customize email content to effectively communicate, ensuring your messages are clear, engaging, and impactful.
                        </div>
                    </span>
                    <div className="top_right_button_container">
                        <div className="top_right_button_container">
                            <button disabled={showLoader} className="btn cus-seconday-bg-btn btn mr-2" onClick={handleSendClick}>
                                Send Email
                            </button>
                            <button disabled={showLoader} className="btn cus-primary-transparent-btn bckbtn" onClick={() => navigate('/email-templates')}>
                                Back
                            </button>
                        </div>
                    </div>
                </div>
                {showLoader && <LoaderSpinner />}
                <div className="cus-page-content-sec">
                    <div className='content-container pt-4'>
                        <div className={classes.formRow}>
                            <div className={classes.label}>Select Recipients:</div>
                            {isSellerOrAdmin() ? (
                                <div className={classes.fieldWrapper}>
                                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, padding: '6px 10px', border: '1px solid #c4c4c4', borderRadius: 4, minHeight: 40, alignItems: 'center', backgroundColor: '#fff', cursor: 'text' }}>
                                        {formData.recipientEmailsList.map((email) => (
                                            <span key={email} style={{ display: 'inline-flex', alignItems: 'center', background: '#e0e0e0', borderRadius: 16, padding: '2px 10px', fontSize: 13 }}>
                                                {email}
                                                <button className='sortBtn' onClick={() => removeEmail(email)} style={{padding:'4px', marginLeft: 6, cursor: 'pointer', fontSize: 16, lineHeight: 1, color: '#555' }}>&times;</button>
                                            </span>
                                        ))}
                                        <input
                                            type="text"
                                            value={emailInput}
                                            onChange={(e) => setEmailInput(e.target.value)}
                                            onKeyDown={handleEmailInputKeyDown}
                                            onBlur={() => emailInput.trim() && addEmail(emailInput.trim())}
                                            placeholder={formData.recipientEmailsList.length === 0 ? 'Type email and press Enter or comma' : ''}
                                            style={{ border: 'none', outline: 'none', flex: 1, minWidth: 180, fontSize: 14, background: 'transparent' }}
                                        />
                                    </div>
                                </div>
                            ) : (
                                <div className={classes.fieldWrapper}>
                                    <FormControl variant="outlined" className={classes.select} size="small">
                                        <ReactSelect
                                        value={formData.recipients}
                                        isMulti
                                        options={recipientList}
                                        onChange={(value) => handleMultipleSelectChange(value, 'recipients')}
                                    />
                                    </FormControl>
                                </div>
                            )}

                        </div>

                        <div className={classes.formRow}>
                            <div className={classes.label}>Select Template</div>
                            <div className={classes.fieldWrapper}>
                                <Select
                                    variant="outlined"
                                    size="small"
                                    className={classes.select}
                                    value={formData.selectedTemplate}
                                    onChange={(e) => handleTemplateSelect(e)}
                                    displayEmpty
                                    renderValue={(value) => value || <em style={{ color: '#aaa' }}>Select Template</em>}
                                >
                                    <MenuItem value="">
                                        None
                                    </MenuItem>
                                    {templateList.map((template) => (
                                        <MenuItem key={template.id} value={template.name}>
                                            {template.name}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </div>
                        </div>

                        <div className={classes.formRow}>
                            <div className={classes.label}>Subject:</div>
                            <div className={classes.fieldWrapper}>
                                <TextField
                                    variant="outlined"
                                    size="small"
                                    className={classes.textField}
                                    value={formData.subject}
                                    onChange={(e) => handleChange('subject', e.target.value)}
                                />
                            </div>
                        </div>

                        <div className={classes.formRow}>
                            <div className={classes.label}>Body:</div>
                            <div className={classes.quillWrapper}>
                                {!isSellerOrAdmin() && (
                                    <div style={{ display: 'flex', alignItems: 'center', marginBottom: 8, gap: 8 }}>
                                        <span style={{ fontSize: 13, color: '#555', fontWeight: 500 }}>Insert Variable:</span>
                                        <FormControl variant="outlined" size="small" style={{ minWidth: 200 }}>
                                            <Select
                                                displayEmpty
                                                onChange={(e) => {
                                                    insertDynamicValue(e.target.value);
                                                }}
                                                renderValue={() => 'Select a variable'}
                                            >
                                                {emailPlaceholderList.map((v) => (
                                                    <MenuItem key={v.value} value={v.value}>
                                                        {v.label} &nbsp;<span style={{ color: '#888', fontSize: 12 }}>{v.value}</span>
                                                    </MenuItem>
                                                ))}
                                            </Select>
                                        </FormControl>
                                    </div>
                                )}
                                <ReactQuill
                                    ref={quillRef}
                                    theme="snow"
                                    value={formData.body}
                                    onChange={(value) => handleChange('body', value)}
                                    modules={QUILL_MODULES}
                                />
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            <AlertDialogModal
                modalShow={showAlert}
                handleCancelDeleteAlert={() => setShowAlert(false)}
                handleYesDeleteAlert={handleSend}
                headingText="Confirm Send"
                messageText="Are you sure you want to send this email?"
            />
        </main>
    );
}
 
export default ComposeEmail;